import React, { FC, useMemo } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { emailValidityRegex } from 'src/shared/helpers/emailValidator';
import {
    DeprecatedButtonGroup,
    HeadingText50,
    BodyTextMedium,
    Checkbox,
    pxToRem,
} from '@modernhealth/vital-components';
import { useSelector } from 'react-redux';
import { getSortedLanguages } from 'src/modules/languages/selectors/languageSelectors';
import { Language } from 'src/modules/languages/models/language';
import {
    renderInputWithFormik,
    renderSelectWithFormik,
} from 'src/modules/dependents/components/FormFields';
import { ScreenContainer } from 'src/modules/dependents/components/ScreenContainer';
import { Trans, useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { MHTermsLink } from 'src/modules/components/MHTermsLink';

export interface JoinModernHealthFormValues {
    firstName: string;
    lastName: string;
    language: string;
    email: string;
    acceptTerms?: boolean;
}

const formSchema = Yup.object<JoinModernHealthFormValues>().shape({
    firstName: Yup.string().trim().required('form.validation.firstName.required'),
    lastName: Yup.string().trim().required('form.validation.lastName.required'),
    language: Yup.string().trim().required('form.validation.language.required'),
    acceptTerms: Yup.bool().required(),
    email: Yup.string()
        .trim()
        .matches(emailValidityRegex, 'form.validation.email.invalid')
        .required('form.validation.email.required'),
});

interface JoinModernHealthFormProps {
    onCancel: (buttonCopy: string) => void;
    onSubmit: (formData: JoinModernHealthFormValues, buttonCopy: string) => void;
    isLoading: boolean;
    initialValues: JoinModernHealthFormValues;
}

export const JoinModernHealthForm: FC<JoinModernHealthFormProps> = ({
    onCancel,
    onSubmit,
    isLoading,
    initialValues,
}) => {
    const { t } = useTranslation();
    const sortedLanguages = useSelector(getSortedLanguages);
    const submitBtnCopy = t('dependents:common.send_invite');

    const languageOptions = useMemo(
        () =>
            sortedLanguages.map((language: Language) => ({
                label: language.displayName,
                value: language.value,
            })),
        [sortedLanguages],
    );

    return (
        <ScreenContainer>
            <HeadingText50 marginBottom={24}>{t('dependents:join.title')}</HeadingText50>
            <BodyTextMedium marginBottom={60}>{t('dependents:join.description')}</BodyTextMedium>
            <Formik<JoinModernHealthFormValues>
                validationSchema={formSchema}
                initialValues={initialValues}
                onSubmit={values => onSubmit(values, submitBtnCopy)}
                validateOnMount
            >
                {props => {
                    const renderSelect = renderSelectWithFormik(props, t);
                    const renderInput = renderInputWithFormik(props, t);

                    return (
                        <>
                            {renderInput('firstName')}
                            {renderInput('lastName')}
                            {renderSelect('language', languageOptions)}
                            {renderInput('email')}
                            <CheckboxContainer>
                                <BodyTextMedium bold marginBottom={8} fontColor="black">
                                    {t('shared:modern_health.terms_and_conditions')}
                                </BodyTextMedium>
                                <BodyTextMedium marginBottom={15}>
                                    <Trans
                                        t={t}
                                        i18nKey="dependents:request_therapy.terms_of_use"
                                        components={{
                                            'terms-of-use-link': <MHTermsLink />,
                                        }}
                                    />
                                </BodyTextMedium>
                                <Checkbox
                                    label={t('shared:modern_health.agree_to_terms_of_use')}
                                    onChange={() =>
                                        props.setFieldValue(
                                            'acceptTerms',
                                            !props.values.acceptTerms,
                                        )
                                    }
                                    value={props.values.acceptTerms ?? false}
                                />
                            </CheckboxContainer>
                            <DeprecatedButtonGroup
                                firstButtonText={t('dependents:common.back')}
                                onFirstButtonClick={() => onCancel(t('dependents:common.back'))}
                                secondButtonText={submitBtnCopy}
                                onSecondButtonClick={props.submitForm}
                                secondButtonDisabled={!props.values.acceptTerms || !props.isValid}
                                firstButtonLoading={isLoading}
                                secondButtonLoading={isLoading}
                                marginTop={30}
                            />
                        </>
                    );
                }}
            </Formik>
        </ScreenContainer>
    );
};

const CheckboxContainer = styled.div`
    margin: ${pxToRem(45, 0)};
`;
